import { ColumnMain } from "#/models/column-main";
import { Column } from "#/models/column";
import { Trans } from "@lingui/macro";
import React from "react";
import { Tag } from "antd";

const AutoCheckModel = (): ColumnMain => {

    const eudrDecreeOptions = [
        { value: 'ARTICLE_9_D', label: <Trans>Article 9(1)(d)</Trans> },
        { value: 'ARTICLE_9_G', label: <Trans>Article 9(1)(g)</Trans> },
        { value: 'ARTICLE_9_H', label: <Trans>Article 9(1)(h)</Trans> },
        { value: 'ARTICLE_10_A', label: <Trans>Article 10(2)(a)</Trans> },
        { value: 'ARTICLE_10_B', label: <Trans>Article 10(2)(b)</Trans> },
        { value: 'ARTICLE_10_C', label: <Trans>Article 10(2)(c)</Trans> },
        { value: 'ARTICLE_10_D', label: <Trans>Article 10(2)(d)</Trans> },
        { value: 'ARTICLE_10_E', label: <Trans>Article 10(2)(e)</Trans> },
        { value: 'ARTICLE_10_F', label: <Trans>Article 10(2)(f)</Trans> },
        { value: 'ARTICLE_10_G', label: <Trans>Article 10(2)(g)</Trans> },
        { value: 'ARTICLE_10_H', label: <Trans>Article 10(2)(h)</Trans> },
        { value: 'ARTICLE_10_J', label: <Trans>Article 10(2)(j)</Trans> },
        { value: 'ARTICLE_10_N', label: <Trans>Article 10(2)(n)</Trans> }
    ];

    const getEudrDecreeLabel = (code: string): React.ReactNode => {
        const option = eudrDecreeOptions.find(o => o.value === code);
        return option ? option.label : code;
    };

    const columns: Array<Column> = new Array();

    columns.push({ key: 'id', width: 80, filter: true, sorter: true, title: <Trans>ID</Trans> });
    columns.push({ key: 'supplier.code', dataIndex: ['supplier', 'code'], width: 150, filter: true, sorter: true, title: <Trans>Supplier Code</Trans> });
    columns.push({ key: 'supplier.name', dataIndex: ['supplier', 'name'], width: 250, filter: true, sorter: true, title: <Trans>Supplier Name</Trans> });
    columns.push({ key: 'country', width: 150, filter: true, sorter: true, title: <Trans>Country</Trans> });
    columns.push({
        key: 'eudrDecrees',
        width: 190,
        sorter: true,
        title: <span style={{ whiteSpace: 'nowrap' }}><Trans>EUDR Decrees</Trans></span>,
        uniqueSelectFilterData: [
            {
                label: <Trans>All</Trans>,
                value: null,
            },
            ...eudrDecreeOptions
        ],
        render: text => {
            if (!text) {
                return '';
            }
            const codes = String(text)
                .split(',')
                .map(code => code.trim())
                .filter(code => code.length > 0);

            return (
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                    {codes.map(code => (
                        <Tag color="geekblue" key={code}>{getEudrDecreeLabel(code)}</Tag>
                    ))}
                </span>
            );
        }
    });
    columns.push({ key: 'locationName', dataIndex: ['location', 'name'], width: 250, filter: true, sorter: true, title: <Trans>Location</Trans> });
    columns.push({
        key: 'type',
        uniqueSelectFilterData: [
            {
                label: <Trans>All</Trans>,
                value: null,
            },
            {
                label: <Trans>COUNTRY_BENCHMARK</Trans>,
                value: 'COUNTRY_BENCHMARK',
            },
            {
                label: <Trans>TRANSPARENCY</Trans>,
                value: 'TRANSPARENCY',
            },
            {
                label: <Trans>FREEDOM_HOUSE</Trans>,
                value: 'FREEDOM_HOUSE',
            },
            {
                label: <Trans>WORLD_BANK_CONTROL_OF_CORRUPTION</Trans>,
                value: 'WORLD_BANK_CONTROL_OF_CORRUPTION',
            },
            {
                label: <Trans>WORLD_BANK_GOVERNMENT_EFFECTIVENESS</Trans>,
                value: 'WORLD_BANK_GOVERNMENT_EFFECTIVENESS',
            },
            {
                label: <Trans>WORLD_BANK_POLITICAL_STABILITY</Trans>,
                value: 'WORLD_BANK_POLITICAL_STABILITY',
            },
            {
                label: <Trans>WORLD_BANK_REGULATORY_QUALITY</Trans>,
                value: 'WORLD_BANK_REGULATORY_QUALITY',
            },
            {
                label: <Trans>LANDMARK_MAP_INDIGENOUS_PRESENCE</Trans>,
                value: 'LANDMARK_MAP_INDIGENOUS_PRESENCE',
            },
            {
                label: <Trans>LANDMARK_MAP_NOT_FORMALLY_RECOGNIZED</Trans>,
                value: 'LANDMARK_MAP_NOT_FORMALLY_RECOGNIZED',
            },
            {
                label: <Trans>LANDMARK_MAP_FORMALLY_RECOGNIZED</Trans>,
                value: 'LANDMARK_MAP_FORMALLY_RECOGNIZED',
            },
            {
                label: <Trans>EU_COUNTRY_BENCHMARK</Trans>,
                value: 'EU_COUNTRY_BENCHMARK',
            },
            {
                label: <Trans>GLOBAL_FOREST_WATCH</Trans>,
                value: 'GLOBAL_FOREST_WATCH',
            },
            {
                label: <Trans>GLOBAL_FOREST_WATCH_INDIGENOUS_LANDS</Trans>,
                value: 'GLOBAL_FOREST_WATCH_INDIGENOUS_LANDS',
            },
            {
                label: <Trans>EU_FOREST_OBSERVATORY</Trans>,
                value: 'EU_FOREST_OBSERVATORY',
            },
            {
                label: <Trans>DEFORESTATION_ASSESSMENT</Trans>,
                value: 'DEFORESTATION_ASSESSMENT',
            },
            {
                label: <Trans>GEOLOCATION_QUALITY</Trans>,
                value: 'GEOLOCATION_QUALITY',
            },
            {
                label: <Trans>FOREST_DEGRADATION</Trans>,
                value: 'FOREST_DEGRADATION',
            },
            {
                label: <Trans>PLANTATION_AND_COMMODITY</Trans>,
                value: 'PLANTATION_AND_COMMODITY',
            },
            {
                label: <Trans>PROTECTED_AREA</Trans>,
                value: 'PROTECTED_AREA',
            }
        ],
        sorter: true,
        width: 400,
        title: <Trans>Type</Trans>,
        render: text => {
            let transText;
            if (text === 'COUNTRY_BENCHMARK') {
                transText = <Trans>COUNTRY_BENCHMARK</Trans>;
            } else if (text === 'TRANSPARENCY') {
                transText = <Trans>TRANSPARENCY</Trans>;
            } else if (text === 'FREEDOM_HOUSE') {
                transText = <Trans>FREEDOM_HOUSE</Trans>;
            } else if (text === 'WORLD_BANK_CONTROL_OF_CORRUPTION') {
                transText = <Trans>WORLD_BANK_CONTROL_OF_CORRUPTION</Trans>;
            } else if (text === 'WORLD_BANK_GOVERNMENT_EFFECTIVENESS') {
                transText = <Trans>WORLD_BANK_GOVERNMENT_EFFECTIVENESS</Trans>;
            } else if (text === 'WORLD_BANK_POLITICAL_STABILITY') {
                transText = <Trans>WORLD_BANK_POLITICAL_STABILITY</Trans>;
            } else if (text === 'WORLD_BANK_REGULATORY_QUALITY') {
                transText = <Trans>WORLD_BANK_REGULATORY_QUALITY</Trans>;
            } else if (text === 'LANDMARK_MAP_INDIGENOUS_PRESENCE') {
                transText = <Trans>LANDMARK_MAP_INDIGENOUS_PRESENCE</Trans>;
            } else if (text === 'LANDMARK_MAP_NOT_FORMALLY_RECOGNIZED') {
                transText = <Trans>LANDMARK_MAP_NOT_FORMALLY_RECOGNIZED</Trans>;
            } else if (text === 'LANDMARK_MAP_FORMALLY_RECOGNIZED') {
                transText = <Trans>LANDMARK_MAP_FORMALLY_RECOGNIZED</Trans>;
            } else if (text === 'EU_COUNTRY_BENCHMARK') {
                transText = <Trans>EU_COUNTRY_BENCHMARK</Trans>;
            } else if (text === 'GLOBAL_FOREST_WATCH') {
                transText = <Trans>GLOBAL_FOREST_WATCH</Trans>;
            } else if (text === 'GLOBAL_FOREST_WATCH_INDIGENOUS_LANDS') {
                transText = <Trans>GLOBAL_FOREST_WATCH_INDIGENOUS_LANDS</Trans>;
            } else if (text === 'EU_FOREST_OBSERVATORY') {
                transText = <Trans>EU_FOREST_OBSERVATORY</Trans>;
            } else if (text === 'DEFORESTATION_ASSESSMENT') {
                transText = <Trans>DEFORESTATION_ASSESSMENT</Trans>;
            } else if (text === 'GEOLOCATION_QUALITY') {
                transText = <Trans>GEOLOCATION_QUALITY</Trans>;
            } else if (text === 'FOREST_DEGRADATION') {
                transText = <Trans>FOREST_DEGRADATION</Trans>;
            } else if (text === 'PLANTATION_AND_COMMODITY') {
                transText = <Trans>PLANTATION_AND_COMMODITY</Trans>;
            } else if (text === 'PROTECTED_AREA') {
                transText = <Trans>PROTECTED_AREA</Trans>;
            }
            return (
                <Tag color="blue" key={text}>
                    {transText}
                </Tag>
            );
        },
    });

    const resultOptions: Array<{ value: string, label: JSX.Element, color: string }> = [
        { value: 'NO_CONCERN', label: <Trans>NO_CONCERN</Trans>, color: 'green' },
        { value: 'MODERATE_CONCERN', label: <Trans>MODERATE_CONCERN</Trans>, color: 'orange' },
        { value: 'SIGNIFICANT_CONCERN', label: <Trans>SIGNIFICANT_CONCERN</Trans>, color: 'red' },
        { value: 'PRESENT', label: <Trans>PRESENT</Trans>, color: 'red' },
        { value: 'NOT_PRESENT', label: <Trans>NOT_PRESENT</Trans>, color: 'green' },
        { value: 'NO_DATA', label: <Trans>NO_DATA</Trans>, color: 'gray' },
        { value: 'TREE_COVER_LOSS_SINCE_2021', label: <Trans>TREE_COVER_LOSS_SINCE_2021</Trans>, color: 'red' },
        { value: 'NO_TREE_COVER_LOSS', label: <Trans>NO_TREE_COVER_LOSS</Trans>, color: 'green' },
        { value: 'INDIGENOUS_LANDS_PRESENT', label: <Trans>INDIGENOUS_LANDS_PRESENT</Trans>, color: 'red' },
        { value: 'INDIGENOUS_LANDS_NOT_PRESENT', label: <Trans>INDIGENOUS_LANDS_NOT_PRESENT</Trans>, color: 'green' },
        { value: 'FOREST_2020', label: <Trans>FOREST_2020</Trans>, color: 'orange' },
        { value: 'NO_FOREST_2020', label: <Trans>NO_FOREST_2020</Trans>, color: 'green' },
        { value: 'DEFORESTATION_DETECTED', label: <Trans>DEFORESTATION_DETECTED</Trans>, color: 'red' },
        { value: 'POSSIBLE_DEFORESTATION', label: <Trans>POSSIBLE_DEFORESTATION</Trans>, color: 'orange' },
        { value: 'DISTURBANCE_DETECTED', label: <Trans>DISTURBANCE_DETECTED</Trans>, color: 'gold' },
        { value: 'NO_DEFORESTATION', label: <Trans>NO_DEFORESTATION</Trans>, color: 'green' },
        { value: 'NEEDS_REVIEW', label: <Trans>NEEDS_REVIEW</Trans>, color: 'orange' },
        { value: 'GEOLOCATION_OK', label: <Trans>GEOLOCATION_OK</Trans>, color: 'green' },
        { value: 'GEOLOCATION_WARNING', label: <Trans>GEOLOCATION_WARNING</Trans>, color: 'orange' },
        { value: 'GEOLOCATION_INVALID', label: <Trans>GEOLOCATION_INVALID</Trans>, color: 'red' },
        { value: 'DEGRADATION_DETECTED', label: <Trans>DEGRADATION_DETECTED</Trans>, color: 'red' },
        { value: 'POSSIBLE_DEGRADATION', label: <Trans>POSSIBLE_DEGRADATION</Trans>, color: 'orange' },
        { value: 'NO_DEGRADATION', label: <Trans>NO_DEGRADATION</Trans>, color: 'green' },
        { value: 'NO_NATURAL_FOREST_2020', label: <Trans>NO_NATURAL_FOREST_2020</Trans>, color: 'green' },
        { value: 'NOT_APPLICABLE', label: <Trans>NOT_APPLICABLE</Trans>, color: 'gray' },
        { value: 'PLANTATION_MATCHES_COMMODITY', label: <Trans>PLANTATION_MATCHES_COMMODITY</Trans>, color: 'green' },
        { value: 'PLANTATION_COMMODITY_MISMATCH', label: <Trans>PLANTATION_COMMODITY_MISMATCH</Trans>, color: 'orange' },
        { value: 'PLANTATION_UNKNOWN_SPECIES', label: <Trans>PLANTATION_UNKNOWN_SPECIES</Trans>, color: 'blue' },
        { value: 'PLANTATION_PRESENT', label: <Trans>PLANTATION_PRESENT</Trans>, color: 'blue' },
        { value: 'NO_PLANTATION', label: <Trans>NO_PLANTATION</Trans>, color: 'gray' },
        { value: 'PROTECTED_AREA_STRICT', label: <Trans>PROTECTED_AREA_STRICT</Trans>, color: 'red' },
        { value: 'PROTECTED_AREA_NATIONAL', label: <Trans>PROTECTED_AREA_NATIONAL</Trans>, color: 'orange' },
        { value: 'PROTECTED_AREA_INTERNATIONAL', label: <Trans>PROTECTED_AREA_INTERNATIONAL</Trans>, color: 'gold' },
        { value: 'PROTECTED_AREA_PRESENT', label: <Trans>PROTECTED_AREA_PRESENT</Trans>, color: 'red' },
        { value: 'NO_PROTECTED_AREA', label: <Trans>NO_PROTECTED_AREA</Trans>, color: 'green' },
        { value: 'LOW_RISK_CATEGORY', label: <Trans>LOW_RISK_CATEGORY</Trans>, color: 'green' },
        { value: 'STANDARD_RISK_CATEGORY', label: <Trans>STANDARD_RISK_CATEGORY</Trans>, color: 'orange' },
        { value: 'HIGH_RISK_CATEGORY', label: <Trans>HIGH_RISK_CATEGORY</Trans>, color: 'red' },
        { value: 'COUNTRY_NOT_FOUND', label: <Trans>COUNTRY_NOT_FOUND</Trans>, color: 'volcano' },
        { value: 'ERROR', label: <Trans>ERROR</Trans>, color: 'volcano' },
        { value: 'PENDING', label: <Trans>PENDING</Trans>, color: 'gray' },
    ];

    columns.push({
        key: 'result',
        uniqueSelectFilterData: [
            {
                label: <Trans>All</Trans>,
                value: null,
            },
            ...resultOptions.map(option => ({ label: option.label, value: option.value }))
        ],
        sorter: true,
        width: 300,
        title: <Trans>Result</Trans>,
        render: text => {
            const option = resultOptions.find(o => o.value === text);
            return (
                <Tag color={option ? option.color : 'gray'} key={text}>
                    {option ? option.label : text}
                </Tag>
            );
        },
    });

    columns.push({ key: 'updatedDate', width: 180, dateTimeFilter: true, sorter: true, title: <Trans>Updated Date</Trans> });
    columns.push({ key: 'updatedBy', width: 200, filter: true, sorter: true, title: <Trans>Updated By</Trans> });
    columns.push({ key: 'createdDate', width: 180, dateTimeFilter: true, sorter: true, title: <Trans>Created Date</Trans>, direction: "desc" });
    columns.push({ key: 'createdBy', width: 200, filter: true, sorter: true, title: <Trans>Created By</Trans> });

    return {
        columns: columns,
        url: "/resource/auto-checks"
    };

}
export default AutoCheckModel();
